import { TableUI } from "components/design/TableUI";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { coaOptions } from "components/Transaction/TransactionColumn";
import { DeleteIcon } from "components/icons/delete";
import { PriceInput } from "components/PriceInput/PriceInput";
import { LIABILITY, REVENUE } from "constants/bookkeeping";
import { ITEM_TYPES, itemTypesOptions } from "constants/productAndServices";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useTypedFormikContext } from "hooks/useTypedFormikContext";
import randomBytes from "randombytes";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetProductCategoriesQuery } from "store/apis/productAndServices";
import { ItemType } from "types/Models/productAndServices";

export type BulkProductReviewRow = {
  id: string;
  row?: number;
  product_name: string;
  price: number;
  type: ItemType;
  category: string;
  transaction_category: string;
};

type BulkProductsFormValues = {
  products: BulkProductReviewRow[];
};

export const createEmptyBulkProductRow = (): BulkProductReviewRow => ({
  id: randomBytes(10).toString("hex"),
  product_name: "",
  price: 0,
  type: ITEM_TYPES.PRODUCT,
  category: "",
  transaction_category: "",
});

const DeleteRow = ({ id }: { id: string }) => {
  const { values, setFieldValue } =
    useTypedFormikContext<BulkProductsFormValues>();

  const onDelete = () => {
    setFieldValue(
      "products",
      values.products.filter((product) => product.id !== id)
    );
  };

  return (
    <Button type="button" customType="ghost_icon" onClick={onDelete}>
      <span className="t-text-text-30">
        <DeleteIcon />
      </span>
    </Button>
  );
};

const CategoryCell = ({ rowIndex }: { rowIndex: number }) => {
  const { values } = useTypedFormikContext<BulkProductsFormValues>();
  const { data: categories = [], isLoading } = useGetProductCategoriesQuery();

  const categoryOptions = categories.map(({ name, uuid }) => ({
    label: name,
    value: uuid,
  }));

  const currentValue = values.products[rowIndex]?.category;
  const selectedOption =
    categoryOptions.find((option) => option.value === currentValue) ||
    (currentValue ? { label: currentValue, value: currentValue } : null);

  return (
    <Combobox
      name={`products[${rowIndex}].category`}
      withForm
      creatable
      hideError
      size="small"
      isLoading={isLoading}
      options={categoryOptions}
      value={selectedOption}
      isClearable
      placeholder="Select or create"
      menuPortalTarget={document.body}
    />
  );
};

const TransactionCategoryCell = ({ rowIndex }: { rowIndex: number }) => {
  const { values } = useTypedFormikContext<BulkProductsFormValues>();
  const { chartOfAccounts, isLoading } = useChartOfAccounts({});

  const revenueAndLiabilityAccounts = chartOfAccounts?.filter(
    ({ identifier_prefix }) =>
      identifier_prefix === REVENUE || identifier_prefix === LIABILITY
  );

  const transactionCategoryOptions = coaOptions(
    revenueAndLiabilityAccounts || []
  );

  const currentValue = values.products[rowIndex]?.transaction_category;
  const selectedOption =
    transactionCategoryOptions.find(
      (option) => option.value === currentValue
    ) || null;

  return (
    <Combobox
      name={`products[${rowIndex}].transaction_category`}
      withForm
      hideError
      size="small"
      isLoading={isLoading}
      isDisabled={isLoading}
      options={transactionCategoryOptions}
      value={selectedOption}
      isClearable
      placeholder="Select category"
      menuPortalTarget={document.body}
    />
  );
};

const createColumn = createColumnHelper<BulkProductReviewRow>();

const columns = [
  createColumn.accessor("product_name", {
    size: 20,
    header: () => <Label required>Name</Label>,
    cell: (info) => (
      <Input
        withForm
        hideError
        required
        customSize="small"
        placeholder="Product name"
        name={`products[${info.row.index}].product_name`}
      />
    ),
  }),

  createColumn.accessor("price", {
    size: 10,
    header: () => <Label required>Price</Label>,
    cell: (info) => (
      <PriceInput
        hideError
        required
        customSize="small"
        placeholder="0.00"
        name={`products[${info.row.index}].price`}
      />
    ),
  }),

  createColumn.accessor("type", {
    size: 10,
    header: () => <Label required>Type</Label>,
    cell: (info) => {
      const rowIndex = info.row.index;
      const selected = itemTypesOptions.find(
        (option) => option.value === info.getValue()
      );
      return (
        <Combobox
          name={`products[${rowIndex}].type`}
          withForm
          hideError
          size="small"
          isClearable={false}
          options={itemTypesOptions}
          value={selected}
          menuPortalTarget={document.body}
        />
      );
    },
  }),

  createColumn.accessor("category", {
    size: 26,
    header: () => <Label required>Group</Label>,
    cell: (info) => <CategoryCell rowIndex={info.row.index} />,
  }),

  createColumn.accessor("transaction_category", {
    size: 30,
    header: () => <Label required>Category</Label>,
    cell: (info) => <TransactionCategoryCell rowIndex={info.row.index} />,
  }),

  createColumn.display({
    id: "ACTIONS",
    size: 4,
    header: "",
    cell: (info) => <DeleteRow id={info.row.original.id} />,
  }),
];

export const BulkProductsReviewTable = () => {
  const { values, setFieldValue } =
    useTypedFormikContext<BulkProductsFormValues>();

  const table = useReactTable({
    data: values.products,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onAddRow = () => {
    setFieldValue("products", [
      ...values.products,
      createEmptyBulkProductRow(),
    ]);
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      <div className="sm:t-overflow-x-auto">
        <div className="sm:t-min-w-[800px]">
          <TableUI table={table} layout="fixed" sticky={false} />
        </div>
      </div>
      <div className="">
        <Button type="button" size="small" onClick={onAddRow}>
          Add more
        </Button>
      </div>
    </div>
  );
};
